import React from "react";
import PdfButton from "../../ui/PdfButton.jsx";
import { boxActionState, boxContents, boxLabel, holdingPalletText, isTaskWipPallet, palletPhoneActions } from "../../../features/salesOrders/palletRules";
import { palletManifestDocument } from "../../../features/salesOrders/palletActions";
import PalletCheckLink from "../../../features/palletCheck/PalletCheckLink.jsx";
import "../../mobile.css";

// One pallet of the order's packing plan on a phone: its state, its boxes (seal a full box, reprint
// a sealed one's label) and what can happen next (features/salesOrders/palletRules.palletPhoneActions).
// Layout only: every button calls onAction(key, args); the screen runs the shared action.
// Optional (the phone package guide, 2026-10-29): `printer` names where labels print (the Laser Zebra by default),
// `summary` a line under the header (pieces, boxes, beacon), `highlight` marks the pallet a scan opened on, and
// `children` adds buttons under the pallet's own (part labels, Find).

const STATUS_CLASS = {
  Building: "jq-status jq-status-pending",
  Ready: "jq-status jq-status-paused",
  Staged: "jq-status jq-status-progress",
  Shipped: "jq-status jq-status-done",
  Consolidated: "jq-status jq-status-cancelled",
};

const ACTION_LABEL = {
  seal: "Seal pallet",
  ship: "Ship pallet",
  stage: "Mark ready for pickup",
  confirmPickup: "Customer here: confirm pickup",
  reprintLabels: (printer) => `Reprint labels at the ${printer}`,
  statusLabel: (printer) => `Status label at the ${printer}`,
};
const actionLabel = (key, printer) => (typeof ACTION_LABEL[key] === "function" ? ACTION_LABEL[key](printer) : ACTION_LABEL[key]);
const PRIMARY = new Set(["seal", "ship", "stage", "confirmPickup"]);

const when = (iso) => (iso ? new Date(iso).toLocaleString([], { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" }) : "");

function BoxRow({ box, pallet, busy, onAction, printer }) {
  const rules = boxActionState(box, { palletStatus: pallet.status, palletShipmentGroupId: pallet.shipment_group_id });
  const label = boxLabel(box);
  const status = box.effective_status || box.status;
  return (
    <div className="jq-task-line flex-column align-items-stretch" data-testid={`so-box-${box.box_id}`}>
      <div className="d-flex align-items-center gap-2">
        <span className="fw-semibold flex-grow-1">
          {box.is_pallet_direct ? "Stack " : "Box "}{label}{box.sku ? <span className="jq-muted"> · {box.sku}</span> : null}
        </span>
        <span className="jq-tag">{status === "FullPerPolicy" ? "Full (policy)" : status}</span>
      </div>
      {boxContents(box).map((r) => (
        <div key={r.part_id || r.product_id} className="jq-muted">
          {r.isAssembly ? r.assembly_name : r.product_id}: {r.displayActual} of {r.displayPlanned}{r.isAssembly ? " assemblies" : ""}
        </div>
      ))}
      {rules.canSeal && (
        <button type="button" className="btn btn-warning mt-2" disabled={!!busy} onClick={() => onAction("sealBox", { box, label })}>
          <i className="fas fa-lock me-2" aria-hidden="true" />Seal {label}
        </button>
      )}
      {rules.canReprintLabel && (
        <button type="button" className="btn btn-outline-secondary mt-2" disabled={!!busy} onClick={() => onAction("reprintBoxLabel", { box, label })}>
          <i className="fas fa-print me-2" aria-hidden="true" />Reprint {label} label at the {printer}
        </button>
      )}
    </div>
  );
}

export default function PalletCard({
  plan, pallet, busy, onAction, openProblems = [], printer = "Laser Zebra", summary = null, highlight = false, children = null,
}) {
  const name = pallet.short_id || `P${pallet.pallet_num}`;
  const actions = palletPhoneActions(pallet, plan.fulfillment_type);
  return (
    <section className={`jq-card${highlight ? " jq-card-highlight" : ""}`} id={`so-pallet-card-${pallet.pallet_id}`} data-testid={`so-pallet-${pallet.pallet_id}`}>
      <div className="d-flex align-items-center gap-2 mb-1">
        <span className="fw-bold flex-grow-1">{pallet.container_type === "Box" ? "Box" : "Pallet"} {name}</span>
        <span className={STATUS_CLASS[pallet.status] || "jq-status"}>{pallet.status}</span>
      </div>
      {summary && <div className="mb-1" data-testid={`so-pallet-summary-${pallet.pallet_id}`}>{summary}</div>}
      {pallet.status !== "Consolidated" && (
        <div className="d-flex flex-wrap align-items-center gap-2 mb-1">
          <PalletCheckLink palletId={pallet.pallet_id} openProblems={openProblems} phone />
        </div>
      )}
      {isTaskWipPallet(pallet)
        ? <div className="jq-muted" data-testid={`so-pallet-holding-${pallet.pallet_id}`}>{holdingPalletText(pallet)}</div>
        : pallet.is_wip && <div className="jq-muted">WIP: staging for {pallet.wip_next_station || "the next station"}</div>}
      {pallet.sealed_at && <div className="jq-muted">Sealed {when(pallet.sealed_at)}{pallet.sealed_by ? ` by ${pallet.sealed_by}` : ""}</div>}
      {pallet.staged_at && <div className="jq-muted">Awaiting pickup since {when(pallet.staged_at)}{pallet.staged_by ? ` (${pallet.staged_by})` : ""}</div>}
      {pallet.picked_up_at && <div className="jq-muted">Picked up {when(pallet.picked_up_at)}{pallet.picked_up_by ? ` by ${pallet.picked_up_by}` : ""}</div>}
      {pallet.shipped_at && !pallet.picked_up_at && <div className="jq-muted">Shipped {when(pallet.shipped_at)}{pallet.shipped_by ? ` by ${pallet.shipped_by}` : ""}</div>}

      <div className="mt-2">
        {(pallet.boxes || []).map((box) => <BoxRow key={box.box_id} box={box} pallet={pallet} busy={busy} onAction={onAction} printer={printer} />)}
        {(pallet.boxes || []).length === 0 && <div className="jq-muted">Nothing on this pallet yet.</div>}
      </div>

      <div className="d-grid gap-2 mt-2">
        {actions.map((a) => {
          if (a.key === "manifest") {
            const doc = palletManifestDocument({ plan, shipmentGroupId: pallet.shipment_group_id, palletNum: pallet.pallet_num });
            return <PdfButton key="manifest" url={doc.url} filename={doc.filename} label={`${name} manifest`} testId={`so-manifest-${pallet.pallet_id}`} />;
          }
          return (
            <React.Fragment key={a.key}>
              <button
                type="button"
                className={`btn ${PRIMARY.has(a.key) ? "btn-primary btn-lg" : "btn-outline-secondary"}`}
                disabled={!!busy || !a.enabled}
                onClick={() => onAction(a.key, { pallet, name })}
              >
                {busy === `${a.key}_${pallet.pallet_id}` && <span className="spinner-border spinner-border-sm me-2" aria-hidden="true" />}
                {actionLabel(a.key, printer)}
              </button>
              {!a.enabled && a.why && <div className="jq-muted">{a.why}</div>}
            </React.Fragment>
          );
        })}
        {children}
      </div>
    </section>
  );
}
